/**
 * Phase 125.33 — Full-Width Weihnachts-Eisenbahn Promo-Banner.
 *
 * Struktur analog zum Rodeo-Banner: Hintergrundbild, Badge (schräg), großer
 * Titel, kursiver Untertitel, 3 Feature-Punkte, roter CTA-Button.
 * Alle Elemente inkl. Schriftart & Schriftgröße im Admin-CMS editierbar.
 *
 * Config aus `settings.home.train_banner`:
 *   { enabled, background_image_url, hero_image_url, logo_image_url,
 *     badge_label, title, subtitle, features, cta_headline, cta_subtext, cta_href,
 *     badge_font_family, badge_font_size, title_font_family, title_font_size,
 *     subtitle_font_family, subtitle_font_size }
 */
import React from 'react';
import * as Icons from 'lucide-react';
import ResponsiveImage from './ResponsiveImage';

const IconRenderer = ({ name, className }) => {
  const IconCmp = Icons[name] || Icons.Sparkles;
  return <IconCmp className={className} aria-hidden="true" />;
};

const DEFAULT_BADGE_FONT = '"Anton", "Bebas Neue", sans-serif';
const DEFAULT_TITLE_FONT = '"Mountains of Christmas", "Lilita One", "Fugaz One", cursive';
const DEFAULT_SUBTITLE_FONT = '"Berkshire Swash", "Caveat Brush", cursive';
const DEFAULT_BADGE_SIZE = 'clamp(0.85rem, 1.4vw, 1.1rem)';
const DEFAULT_TITLE_SIZE = 'clamp(3rem, 8vw, 6.5rem)';
const DEFAULT_SUBTITLE_SIZE = 'clamp(1.3rem, 3vw, 2rem)';

const DEFAULTS = {
  background_image_url:
    'https://customer-assets-jt897jd0.emergentagent.net/job_34531605-61c4-451d-9ee7-b24843ceca0d/artifacts/0beeac6b_ChatGPT%20Image%2019.%20Aug.%202026%2C%2011_30_26.png',
  hero_image_url: '',
  logo_image_url: '',
  badge_label: 'Ho, Ho, Ho — Weihnachtszeit!',
  badge_font_family: '',
  badge_font_size: '',
  title: 'WEIHNACHTSEXPRESS',
  title_font_family: '',
  title_font_size: '',
  subtitle: 'Steigt ein zur großen Winterfahrt!',
  subtitle_font_family: '',
  subtitle_font_size: '',
  features: [
    { icon: 'Baby', title: 'Für Kinder ab 2 Jahren —', subtitle: 'sicher, entspannt & mit Aufsicht' },
    { icon: 'Snowflake', title: 'Perfekt für Weihnachtsmärkte —', subtitle: 'Stadtfeste, Firmen- & Familienfeiern' },
    { icon: 'PackageCheck', title: 'Rundum-Paket —', subtitle: 'Bahn, Schienen & Betreuer inklusive' },
  ],
  cta_headline: 'Jetzt anfragen!',
  cta_subtext: 'Termine begrenzt — sichert Euch Eure Weihnachtsfahrt',
  cta_href: '/kontakt',
};

const HomeTrainBanner = ({ config, topBg, bottomBg, wrapperBg }) => {
  const raw = config || {};
  const cfg = { ...DEFAULTS, ...raw };
  if (raw.enabled === false) return null;

  const features = Array.isArray(cfg.features) && cfg.features.length ? cfg.features : DEFAULTS.features;
  const topFill = topBg === null ? null : (topBg || wrapperBg || '#ffffff');
  const bottomFill = bottomBg === null ? null : (bottomBg || wrapperBg || '#ffffff');

  return (
    <section
      className="relative overflow-hidden"
      data-testid="home-train-banner"
    >
      <div
        className="relative w-full"
        style={{
          backgroundImage: `url("${cfg.background_image_url}")`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
      >
        {/* Weihnachts-Overlay: Rot-Grün Farbverlauf für festliche Stimmung */}
        <div
          className="absolute inset-0 pointer-events-none"
          aria-hidden="true"
          style={{
            background:
              'linear-gradient(180deg, rgba(140, 20, 20, 0.55) 0%, rgba(20, 60, 40, 0.35) 45%, rgba(140, 20, 20, 0.55) 100%)',
          }}
        />

        {/* Top wave */}
        {topFill && (
          <svg
            className="absolute left-0 w-full h-8 md:h-12 block pointer-events-none z-20"
            viewBox="0 0 1440 100"
            preserveAspectRatio="none"
            style={{ display: 'block', transform: 'rotate(180deg)', top: '-2px' }}
            aria-hidden="true"
          >
            <path
              fill={topFill}
              d="M0,50 C 240,50 320,25 480,25 C 640,25 720,50 960,50 C 1200,50 1280,25 1440,25 L1440,100 L0,100 Z"
            />
          </svg>
        )}

        {/* Content */}
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 md:pt-14 pb-14 md:pb-20 z-10">
          {/* Logo oben rechts */}
          {cfg.logo_image_url && (
            <div className="absolute top-4 right-4 md:top-6 md:right-6 w-20 h-20 md:w-28 md:h-28 rounded-2xl overflow-hidden bg-white/5 backdrop-blur-sm ring-2 ring-white/30 shadow-xl">
              <ResponsiveImage
                src={cfg.logo_image_url}
                alt="Logo"
                className="w-full h-full object-contain p-1.5"
              />
            </div>
          )}

          <div className={cfg.hero_image_url ? 'grid grid-cols-1 md:grid-cols-[1.15fr_1fr] gap-6 md:gap-8 items-center' : 'max-w-3xl'}>
            <div className="text-white space-y-4 md:space-y-5">
              <div
                className="inline-block bg-red-800/95 text-white px-5 py-1.5 rounded-md shadow-lg uppercase"
                style={{
                  fontFamily: cfg.badge_font_family || DEFAULT_BADGE_FONT,
                  fontSize: cfg.badge_font_size || DEFAULT_BADGE_SIZE,
                  letterSpacing: '0.05em',
                  transform: 'rotate(-2deg) skew(-8deg)',
                }}
                data-testid="train-badge"
              >
                {cfg.badge_label}
              </div>
              <div
                role="heading"
                aria-level="2"
                className="leading-none text-white drop-shadow-[0_4px_10px_rgba(0,0,0,0.6)]"
                style={{
                  fontFamily: cfg.title_font_family || DEFAULT_TITLE_FONT,
                  fontSize: cfg.title_font_size || DEFAULT_TITLE_SIZE,
                  letterSpacing: '0.02em',
                  fontWeight: 400,
                  textTransform: 'uppercase',
                  transform: 'skew(-4deg)',
                  transformOrigin: 'left center',
                }}
                data-testid="train-title"
              >
                {cfg.title}
              </div>
              <div
                className="text-yellow-300 leading-[1.05] drop-shadow-[0_2px_6px_rgba(0,0,0,0.7)]"
                style={{
                  fontFamily: cfg.subtitle_font_family || DEFAULT_SUBTITLE_FONT,
                  fontSize: cfg.subtitle_font_size || DEFAULT_SUBTITLE_SIZE,
                  fontWeight: 400,
                  letterSpacing: '0.01em',
                  maxWidth: '22ch',
                }}
                data-testid="train-subtitle"
              >
                {cfg.subtitle}
              </div>

              <ul className="space-y-2.5 pt-1">
                {features.slice(0, 3).map((f, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <div className="shrink-0 w-8 h-8 md:w-9 md:h-9 rounded-full bg-red-900/80 border-2 border-yellow-300 flex items-center justify-center shadow-lg">
                      <IconRenderer name={f.icon} className="h-4 w-4 md:h-4.5 md:w-4.5 text-yellow-300" />
                    </div>
                    <div
                      className="text-white text-sm md:text-base leading-tight drop-shadow-[0_1px_3px_rgba(0,0,0,0.6)]"
                      style={{ fontFamily: 'inherit' }}
                    >
                      <div className="font-semibold">{f.title}</div>
                      {f.subtitle && (
                        <div className="text-white/85 font-normal">{f.subtitle}</div>
                      )}
                    </div>
                  </li>
                ))}
              </ul>

              {/* CTA-Button direkt unter der Feature-Liste */}
              <div className="pt-4">
                <a
                  href={cfg.cta_href}
                  className="inline-flex flex-col items-center bg-emerald-700 hover:bg-emerald-600 text-white px-5 md:px-7 py-2 md:py-3 rounded-md shadow-2xl transition-transform hover:scale-105"
                  style={{ transform: 'rotate(-1deg)' }}
                  data-testid="train-cta"
                >
                  <span
                    className="font-black uppercase tracking-wide leading-none"
                    style={{ fontSize: 'clamp(1rem, 1.9vw, 1.3rem)' }}
                  >
                    {cfg.cta_headline}
                  </span>
                  {cfg.cta_subtext && (
                    <span className="text-yellow-200 font-bold uppercase text-[9px] md:text-[11px] tracking-wider mt-1">
                      {cfg.cta_subtext}
                    </span>
                  )}
                </a>
              </div>
            </div>

            {cfg.hero_image_url && (
              <div className="relative">
                <ResponsiveImage
                  src={cfg.hero_image_url}
                  alt="Eisenbahn"
                  className="w-full h-auto max-h-[420px] object-contain drop-shadow-2xl"
                />
              </div>
            )}
          </div>
        </div>

        {/* Bottom wave */}
        {bottomFill && (
          <svg
            className="absolute left-0 w-full h-8 md:h-12 block pointer-events-none z-20"
            viewBox="0 0 1440 100"
            preserveAspectRatio="none"
            style={{ display: 'block', bottom: '-2px' }}
            aria-hidden="true"
          >
            <path
              fill={bottomFill}
              d="M0,50 C 240,50 320,25 480,25 C 640,25 720,50 960,50 C 1200,50 1280,25 1440,25 L1440,100 L0,100 Z"
            />
          </svg>
        )}
      </div>
    </section>
  );
};

export default HomeTrainBanner;
